/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Global listeners gated by REACT_APP_LEGACY_UPLOAD_COMPAT (default OFF).
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * usePickerHandoff — universal picker handoff diagnostic + recovery
 * primitive. Every upload entry point (single-file pickers AND the
 * multi-clip basket) wires this in so:
 *
 *   1. We KNOW the picker click actually fired (handoff_attempt
 *      event surfaces in the operator-visible debug panel).
 *
 *   2. We KNOW whether onChange ever fired after Photos closed
 *      (handoff_onchange / handoff_empty events).
 *
 *   3. We DETECT the "Photos handoff attempted but onChange never
 *      returned" failure mode — the May 20 root cause that hides
 *      from console logs because nothing throws. When the parent
 *      surface re-mounts / re-focuses with NO onChange fired and
 *      NO files staged, we surface a `recoveryHint` so the entry
 *      point can render a visible "Did you just pick from Photos?
 *      Tap to re-pick." panel — no silent failure.
 *
 * Why a separate hook from useBasketAutoReopen:
 *   • useBasketAutoReopen recovers SERVER-BACKED state (a basket
 *     with a basket_id). It's only useful for ClipBasketModal.
 *   • usePickerHandoff recovers LOCAL state (the operator's intent
 *     to pick a file). It works for single-file pickers too —
 *     because a File object can never survive an iPhone evict,
 *     but the operator's INTENT to pick can be re-surfaced.
 *
 * Single-file entry points get usePickerHandoff alone.
 * The basket modal gets BOTH hooks.
 *
 * Wire-up at the entry point:
 *
 *   const picker = usePickerHandoff({
 *     source: "lesson_review_single",      // unique tag
 *     hasStagedFiles: () => !!file,        // closure into local state
 *   });
 *
 *   <input
 *     ref={picker.inputRef}
 *     type="file"
 *     onChange={picker.wrapOnChange(myOnPick)}
 *   />
 *
 *   <button onClick={picker.openPicker}>Pick file</button>
 *
 *   {picker.recoveryHint && (
 *     <RecoverableCallout
 *       message="Did you just pick from Photos? It looks like we lost the selection."
 *       onRePick={picker.openPicker}
 *       onDismiss={picker.clearRecovery}
 *     />
 *   )}
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { emitPickerEvent } from "../lib/pickerDebug";
import { legacyUploadCompatEnabled } from "../lib/legacyUploadCompat";

const ATTEMPT_KEY_PREFIX = "ndg:picker-attempt:v1:";
const ATTEMPT_TTL_MS = 90_000;
// Time window after an attempt within which a missing onChange
// counts as a "handoff_missed" event rather than the user simply
// cancelling out of Photos. iOS Safari typically returns within 1
// to 8 seconds; we keep a generous lower bound so a slow iCloud
// re-download still counts as a real attempt.
const MISSED_DETECTION_DELAY_MS = 800;

function _safeWindow() {
  return typeof window !== "undefined" ? window : null;
}
function _safeSessionStorage() {
  try { return typeof sessionStorage !== "undefined" ? sessionStorage : null; }
  catch (_) { return null; }
}

export default function usePickerHandoff({
  source,
  hasStagedFiles,
  enabled = true,
}) {
  if (!source) {
    throw new Error("usePickerHandoff requires `source`");
  }
  const inputRef = useRef(null);
  const onChangeFiredRef = useRef(false);
  const [recoveryHint, setRecoveryHint] = useState(null);

  const attemptKey = `${ATTEMPT_KEY_PREFIX}${source}`;

  const _readAttempt = useCallback(() => {
    const ss = _safeSessionStorage();
    if (!ss) return null;
    try {
      const raw = ss.getItem(attemptKey);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj || !obj.at) return null;
      const age = Date.now() - obj.at;
      if (age > ATTEMPT_TTL_MS) {
        ss.removeItem(attemptKey);
        return null;
      }
      return { ...obj, age_ms: age };
    } catch (_) {
      return null;
    }
  }, [attemptKey]);

  const _writeAttempt = useCallback(() => {
    const ss = _safeSessionStorage();
    if (!ss) return;
    const win = _safeWindow();
    const route = win && win.location ? win.location.pathname.slice(0, 200) : null;
    try {
      ss.setItem(
        attemptKey,
        JSON.stringify({ source, route, at: Date.now() }),
      );
    } catch (_) { /* noop */ }
  }, [attemptKey, source]);

  const _clearAttempt = useCallback(() => {
    const ss = _safeSessionStorage();
    if (!ss) return;
    try { ss.removeItem(attemptKey); } catch (_) { /* noop */ }
  }, [attemptKey]);

  // ── openPicker — call this from your button onClick ───────────
  const openPicker = useCallback(() => {
    if (!enabled) return;
    onChangeFiredRef.current = false;
    _writeAttempt();
    const win = _safeWindow();
    emitPickerEvent("handoff_attempt", {
      source,
      route: win && win.location ? win.location.pathname : null,
    });
    inputRef.current?.click?.();
  }, [enabled, source, _writeAttempt]);

  // ── wrapOnChange — wraps your existing picker handler ─────────
  const wrapOnChange = useCallback((handler) => {
    return (e) => {
      onChangeFiredRef.current = true;
      const files = Array.from(e?.target?.files || []);
      if (files.length === 0) {
        emitPickerEvent("handoff_empty", { source });
      } else {
        emitPickerEvent("handoff_onchange", {
          source,
          file_count: files.length,
          first_size: files[0]?.size || 0,
          first_type: files[0]?.type || null,
        });
      }
      _clearAttempt();
      try { handler?.(e, files); } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[picker] onChange handler threw", err);
      }
    };
  }, [source, _clearAttempt]);

  // ── markFilesStaged — call this once your component has placed
  //    files into local/staged state. Surfaces the chain end so
  //    the operator can confirm "files reached the UI".
  const markFilesStaged = useCallback((count) => {
    emitPickerEvent("handoff_files_staged", {
      source,
      file_count: count || 0,
    });
  }, [source]);

  // ── Recovery detection: on mount + pageshow + focus + visibility,
  //    if a fresh attempt exists AND onChange never fired AND the
  //    component currently has no staged files, surface a recovery
  //    hint and emit handoff_missed.
  useEffect(() => {
    if (!enabled) return undefined;
    // LEGACY_DECOMMISSIONED — quarantine gate. Default OFF.
    // Picker recovery listeners are armed only when
    // REACT_APP_LEGACY_UPLOAD_COMPAT=true.
    if (!legacyUploadCompatEnabled()) return undefined;
    const win = _safeWindow();
    if (!win) return undefined;

    const check = (trigger) => {
      // Give the React commit queue a tick to let an in-flight
      // onChange land before we declare "missed".
      setTimeout(() => {
        const att = _readAttempt();
        if (!att) return;
        if (onChangeFiredRef.current) return;
        // Use the closure-captured hasStagedFiles so we read fresh
        // state at trigger time, not at mount time.
        try {
          if (typeof hasStagedFiles === "function" && hasStagedFiles()) {
            // Files already in component state — recovery not needed.
            _clearAttempt();
            return;
          }
        } catch (_) { /* defensive */ }
        emitPickerEvent("handoff_missed", {
          source,
          trigger,
          age_ms: att.age_ms,
          route: att.route || null,
        });
        setRecoveryHint({
          at: Date.now(),
          source,
          trigger,
          age_ms: att.age_ms,
        });
      }, MISSED_DETECTION_DELAY_MS);
    };

    // Run once on mount in case we ARE the post-handoff remount.
    check("mount");

    const onPageShow = () => check("pageshow");
    const onFocus = () => check("focus");
    const onVisibility = () => {
      if (typeof document !== "undefined" && !document.hidden) {
        check("visibilitychange");
      }
    };
    win.addEventListener("pageshow", onPageShow);
    win.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      win.removeEventListener("pageshow", onPageShow);
      win.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, source, hasStagedFiles, _readAttempt, _clearAttempt]);

  const clearRecovery = useCallback(() => {
    setRecoveryHint(null);
    _clearAttempt();
  }, [_clearAttempt]);

  return {
    inputRef,
    openPicker,
    wrapOnChange,
    markFilesStaged,
    recoveryHint,
    clearRecovery,
  };
}
